import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Award, BookOpen, Briefcase, CheckCircle, Clock, Copy, ExternalLink, Filter, Search, Users } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
export function BenefitHistoryPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Mock history data
  const history = [{
    id: 1,
    title: 'Diskon Pelatihan & Workshop',
    category: 'learning',
    provider: 'ATIOS Academy',
    code: 'ATIOS-LEARN-2025-XYZ',
    claimedDate: '15 Jan 2025',
    validUntil: '31 Des 2025',
    status: 'active',
    icon: BookOpen
  }, {
    id: 2,
    title: 'Akses Bursa Lowongan Premium',
    category: 'career',
    provider: 'ATIOS Career Center',
    code: 'AUTO-ACTIVATED',
    claimedDate: '10 Jan 2025',
    validUntil: '31 Des 2025',
    status: 'active',
    icon: Briefcase
  }, {
    id: 3,
    title: 'Webinar Series: AI Future',
    category: 'learning',
    provider: 'Tech Partner Corp',
    code: 'WEBINAR-AI-001',
    claimedDate: '01 Des 2024',
    validUntil: '15 Des 2024',
    status: 'used',
    icon: BookOpen
  }, {
    id: 4,
    title: 'Voucher Konferensi Tahunan',
    category: 'networking',
    provider: 'ATIOS Events',
    code: 'CONF-2024-EARLY',
    claimedDate: '15 Nov 2024',
    validUntil: '30 Nov 2024',
    status: 'expired',
    icon: Users
  }];
  const filteredHistory = history.filter(item => {
    const matchesTab = activeTab === 'all' || item.status === activeTab;
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });
  const stats = [{
    label: 'Total Diklaim',
    value: history.length.toString(),
    icon: Award,
    color: 'text-atios-primary',
    bg: 'bg-atios-light'
  }, {
    label: 'Manfaat Aktif',
    value: history.filter(h => h.status === 'active').length.toString(),
    icon: CheckCircle,
    color: 'text-success',
    bg: 'bg-success-light'
  }, {
    label: 'Telah Digunakan',
    value: history.filter(h => h.status === 'used').length.toString(),
    icon: Clock,
    color: 'text-neutral-600',
    bg: 'bg-neutral-100'
  }];
  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    alert('Kode berhasil disalin!');
  };
  return <div className="min-h-screen bg-white" data-id="element-1186">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8" data-id="element-1187">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1188">
          <div data-id="element-1189">
            <Button variant="ghost" onClick={() => navigate('/benefits')} className="mb-2 pl-0 hover:pl-2 transition-all" data-id="element-1190">
              <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1191" />
              Kembali ke Daftar Manfaat
            </Button>
            <h1 className="text-3xl font-semibold text-neutral-900" data-id="element-1192">
              Riwayat Manfaat
            </h1>
            <p className="text-neutral-600 mt-1" data-id="element-1193">
              Daftar manfaat yang telah Anda klaim dan status penggunaannya
            </p>
          </div>
          <Button onClick={() => navigate('/benefits')} data-id="element-1194">
            <Award className="w-4 h-4 mr-2" data-id="element-1195" />
            Klaim Manfaat Baru
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1196">
          {stats.map((stat, i) => <Card key={i} data-id="element-1197">
              <CardContent className="p-6" data-id="element-1198">
                <div className="flex items-center gap-4" data-id="element-1199">
                  <div className={`h-12 w-12 rounded-lg ${stat.bg} flex items-center justify-center`} data-id="element-1200">
                    <stat.icon className={`h-6 w-6 ${stat.color}`} data-id="element-1201" />
                  </div>
                  <div data-id="element-1202">
                    <p className="text-2xl font-semibold text-neutral-900" data-id="element-1203">
                      {stat.value}
                    </p>
                    <p className="text-sm text-neutral-600" data-id="element-1204">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>)}
        </div>

        {/* Filters & Tabs */}
        <div className="space-y-6" data-id="element-1205">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-1206">
            <div className="flex-1 relative" data-id="element-1207">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" data-id="element-1208" />
              <Input placeholder="Cari riwayat manfaat..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-id="element-1209" />
            </div>
            <Button variant="secondary" data-id="element-1210">
              <Filter className="w-4 h-4 mr-2" data-id="element-1211" />
              Filter
            </Button>
          </div>

          <Tabs defaultValue="all" className="w-full" data-id="element-1212">
            <TabsList className="bg-neutral-100 border border-neutral-200" data-id="element-1213">
              <TabsTrigger value="all" onClick={() => setActiveTab('all')} data-id="element-1214">
                Semua
              </TabsTrigger>
              <TabsTrigger value="active" onClick={() => setActiveTab('active')} data-id="element-1215">
                Aktif
              </TabsTrigger>
              <TabsTrigger value="used" onClick={() => setActiveTab('used')} data-id="element-1216">
                Digunakan
              </TabsTrigger>
              <TabsTrigger value="expired" onClick={() => setActiveTab('expired')} data-id="element-1217">
                Kadaluarsa
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-6" data-id="element-1218">
              {filteredHistory.length === 0 ? <div className="text-center py-12 bg-neutral-50 rounded-lg border border-neutral-200 border-dashed" data-id="element-1219">
                  <Award className="w-12 h-12 text-neutral-300 mx-auto mb-4" data-id="element-1220" />
                  <h3 className="text-lg font-medium text-neutral-900 mb-2" data-id="element-1221">
                    Belum ada riwayat
                  </h3>
                  <p className="text-neutral-600 mb-6" data-id="element-1222">
                    Anda belum memiliki riwayat klaim manfaat untuk kategori
                    ini.
                  </p>
                  <Button onClick={() => navigate('/benefits')} data-id="element-1223">
                    Jelajahi Manfaat
                  </Button>
                </div> : <div className="grid grid-cols-1 gap-4" data-id="element-1224">
                  {filteredHistory.map(item => <Card key={item.id} className="hover:shadow-md transition-shadow" data-id="element-1225">
                      <CardContent className="p-6" data-id="element-1226">
                        <div className="flex flex-col md:flex-row md:items-center gap-6" data-id="element-1227">
                          {/* Icon */}
                          <div className="h-12 w-12 rounded-lg bg-atios-light flex items-center justify-center flex-shrink-0" data-id="element-1228">
                            <item.icon className="h-6 w-6 text-atios-primary" data-id="element-1229" />
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0" data-id="element-1230">
                            <div className="flex items-start justify-between mb-2" data-id="element-1231">
                              <div data-id="element-1232">
                                <h3 className="font-semibold text-neutral-900 text-lg" data-id="element-1233">
                                  {item.title}
                                </h3>
                                <p className="text-sm text-neutral-600" data-id="element-1234">
                                  {item.provider}
                                </p>
                              </div>
                              <Badge variant={item.status === 'active' ? 'success' : item.status === 'used' ? 'default' : 'error'} className="capitalize" data-id="element-1235">
                                {item.status === 'active' ? 'Aktif' : item.status === 'used' ? 'Digunakan' : 'Kadaluarsa'}
                              </Badge>
                            </div>

                            <div className="flex flex-wrap gap-4 text-sm text-neutral-600 mt-2" data-id="element-1236">
                              <div className="flex items-center gap-1" data-id="element-1237">
                                <Clock className="w-4 h-4" data-id="element-1238" />
                                <span data-id="element-1239">Klaim: {item.claimedDate}</span>
                              </div>
                              <div className="flex items-center gap-1" data-id="element-1240">
                                <Clock className="w-4 h-4" data-id="element-1241" />
                                <span data-id="element-1242">Berlaku s/d: {item.validUntil}</span>
                              </div>
                            </div>
                          </div>

                          {/* Action / Code */}
                          <div className="flex flex-col items-end gap-3 min-w-[200px]" data-id="element-1243">
                            {item.status === 'active' && <div className="w-full bg-neutral-50 border border-neutral-200 rounded-md p-2 flex items-center justify-between gap-2" data-id="element-1244">
                                <code className="text-sm font-mono font-bold text-atios-primary" data-id="element-1245">
                                  {item.code}
                                </code>
                                <button onClick={() => copyCode(item.code)} className="text-neutral-400 hover:text-neutral-600" title="Salin" data-id="element-1246">
                                  <Copy className="w-4 h-4" data-id="element-1247" />
                                </button>
                              </div>}
                            <div className="flex gap-2 w-full" data-id="element-1248">
                              <Button variant="secondary" size="sm" className="flex-1" onClick={() => navigate(`/benefits/${item.id}`)} data-id="element-1249">
                                Detail
                              </Button>
                              {item.status === 'active' && <Button size="sm" className="flex-1" data-id="element-1250">
                                  Gunakan{' '}
                                  <ExternalLink className="w-3 h-3 ml-1" data-id="element-1251" />
                                </Button>}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>)}
                </div>}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>;
}